import React, { useRef, useEffect } from 'react'; // <-- Step 1: Import useEffect
import { motion } from 'framer-motion';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { useGSAP } from '@gsap/react';

// Import your existing components and assets
import Toptext from '../../components/Toptext';
import Globe from '../../assets/icons/globe.svg';
import Button1 from '../../components/ui/Button1';
import Image1 from '../../assets/images/image1.png';
import Image2 from '../../assets/images/hero-main.png';
import DynamicHeading from '../../components/ui/CustomHeading';
import JBSLogo from '../../assets/logo.png';
import AgroLogo from "../../assets/agroLogo.png";
import { CompaniesCardItem } from '../../components/CompaniesCardItem';
import LogoTicker from '../../components/LogoTicker';
import AgTronImage from "../../assets/images/AgTronRover.webp";
import Button2 from '../../components/ui/Button2';
import SSAMImage from "../../assets/images/smart-dashboard 1.png";
import IndiaFlag from "../../assets/images/india-flag.png";
import UKFlag from "../../assets/images/uk-flag.png";
import SubHeading from '../../components/ui/SubHeading';
import { useNavigate } from 'react-router-dom';
// Register the GSAP ScrollTrigger plugin
gsap.registerPlugin(ScrollTrigger);
// const navigate = useNavigate();
// --- DATA (Unchanged) ---
const groupCompanies = [
    {
        logo: JBSLogo,
        title: 'JBS Capacitors Pvt Ltd (India)',
        description: 'Leading manufacturer of high-performance capacitors in India, delivering reliable energy solutions for industrial and electronic applications'
    },
    {
        logo: JBSLogo,
        title: 'JBS Robotics & Applied AI Research (UK)',
        description: 'Advancing intelligent automation through cutting-edge research in robotics, machine learning, and applied AI technologies'
    },
    {
        logo: AgroLogo,
        title: 'Agrotronic Farms Ltd (UK)',
        description: 'Innovating sustainable agriculture with smart farming solutions powered by robotics, IoT, and AI-driven analytics.'
    }
];

const Brands = [
    {
        image: AgTronImage,
        logo: JBSLogo,
        title: 'AgTron Rover',
        description: 'An autonomous agricultural rover engineered for precision farming and smart field monitoring.',
        button: 'Learn More',
        onClick: () => window.location.href = "/products&services/AgTron-Workhorse",
      },
      {
        image: SSAMImage,
        logo: JBSLogo,
        title: 'SSAM Robot',
        description: 'A smart autonomous robot designed for real-time surveillance, monitoring, and assistance across diverse environments.',
        button: 'Learn More',
        onClick: () => window.location.href = "/products&services/SAM-Robot",
      },
      {
        image: AgTronImage,
        logo: JBSLogo,
        title: 'AgTron Scout',
        description: 'Aerial surveillance drone for real-time crop health analysis and mapping.',
        button: 'Learn More',
        onClick: () => window.location.href = "/products&services/AgTronGrow",
      },
]

// --- ANIMATED BRAND CARD COMPONENT (Unchanged) ---
const BrandsCardItem = ({ image, logo, title, description, button, onClick }) => {
    return (
        <motion.div
            className="flex flex-col bg-white p-4 sm:p-6 rounded-[28px] sm:rounded-[38px] shadow-lg w-full max-w-[371px] mx-auto"
            whileHover={{ y: -10, scale: 1.03, boxShadow: "0px 20px 30px rgba(0, 0, 0, 0.1)" }}
            transition={{ type: "spring", stiffness: 200, damping: 15 }}
        >
            <div className="relative w-full aspect-[322/313] overflow-hidden">
                <svg width="0" height="0" className="absolute">
                    <defs>
                        <clipPath id="agtronClip" clipPathUnits="objectBoundingBox">
                            <path d="M0.844,0 C0.93,0,1,0.07,1,0.16 V0.561 C1,0.648,0.93,0.718,0.844,0.718 H0.842 C0.79,0.718,0.749,0.76,0.749,0.812 V0.831 C0.749,0.92,0.679,0.99,0.593,0.99 H0.155 C0.069,0.99,0,0.92,0,0.831 V0.16 C0,0.07,0.069,0,0.155,0 H0.844 Z" />
                        </clipPath>
                    </defs>
                </svg>
                <motion.img
                    src={image}
                    alt="main"
                    style={{ clipPath: "url(#agtronClip)" }}
                    className="absolute top-0 left-0 w-full h-full object-cover"
                    whileHover={{ scale: 1.1 }}
                    transition={{ duration: 0.3 }}
                />
                <img
                    src={logo}
                    alt="logo"
                    className="absolute bottom-3 right-3 bg-white/80 backdrop-blur-sm p-1 rounded-xl w-12 h-auto"
                />
            </div>
            <div className="mt-5 px-1">
                <h1 className="text-jbsGray text-xl sm:text-2xl font-black">{title}</h1>
                <p className="text-sm text-gray-600 mt-2 leading-relaxed">{description}</p>
            </div>
            <div className="mt-auto pt-3">
                <Button2 width="100%" onClick={onClick}>
                    {button}
                </Button2>
            </div>
        </motion.div>
    );
}

// --- MAIN HOMEPAGE COMPONENT WITH FIXES ---
const HomePage = () => {
    const mainRef = useRef(null);
    const navigate = useNavigate();
    // --- Step 2: Add useEffect to scroll to top on component mount ---
    useEffect(() => {
        window.scrollTo(0, 0);
        document.title = 'JBS Capacitors';
    }, []); // The empty dependency array [] means this effect runs only once after the initial render.

    useGSAP(() => {
        const tl = gsap.timeline({ defaults: { ease: "power3.inOut", duration: 0.8 } });

        tl.from(".hero-toptext", { y: -30, opacity: 0, duration: 0.6 })
          // The selector is updated to be more robust, targeting spans inside our new container
          .from(".hero-heading-container span", { y: 50, opacity: 0, stagger: 0.1 }, "-=0.2")
          .from(".hero-flags", { scale: 0, opacity: 0, stagger: 0.2, ease: "back.out(1.7)" }, "-=0.5")
          .from(".hero-button", { y: 30, opacity: 0 }, "-=0.6")
          .from(".hero-image-main", { scale: 1.2, opacity: 0, duration: 1.2 }, "-=0.8")
          .from(".hero-image-side", { x: -50, opacity: 0, stagger: 0.2 }, "-=1");

        const animateOnScroll = (selector, props = { y: 50, opacity: 0 }) => {
            gsap.from(selector, {
                ...props,
                stagger: 0.2,
                duration: 0.8,
                ease: "power2.out",
                scrollTrigger: {
                    trigger: selector,
                    start: "top 85%",
                    toggleActions: "play none none none",
                }
            });
        };

        animateOnScroll(".group-companies-heading");
        animateOnScroll(".group-companies-subheading");
        animateOnScroll(".company-card");
        animateOnScroll(".logo-ticker-section");
        animateOnScroll(".brands-heading");
        animateOnScroll(".brands-subheading");
        animateOnScroll(".brand-card", { y: 80, opacity: 0 });

    }, { scope: mainRef });

    const containerVariants = {
        hidden: { opacity: 0 },
        visible: {
            opacity: 1,
            transition: { staggerChildren: 0.2, },
        },
    };

    const itemVariants = {
        hidden: { y: 20, opacity: 0 },
        visible: {
            y: 0,
            opacity: 1,
            transition: { type: 'spring', stiffness: 100 }
        },
    };

    return (
        // --- Step 3: Ensure the main container hides horizontal overflow ---
        <div ref={mainRef} className='mt-32 flex flex-col items-center justify-center '>

            <div className='hero-toptext'>
                <Toptext icon={Globe}>
                    From Bhubaneswar to the World — Robotics Made for Everyone.
                </Toptext>
            </div>

            {/* --- Step 4: Add overflow-hidden to the container with elements that might overflow --- */}
            <div className='mt-12 text-center overflow-hidden'> 
                <div className="flex mx-auto max-w-[90%] sm:max-w-[85%] md:max-w-[75%] lg:max-w-[70%] xl:max-w-[60%]">
                    {/* Wrapped DynamicHeading in a div to target its children reliably for animation */}
                    <div className="hero-heading-container">
                        <DynamicHeading
                            parts={[
                                { text: 'Future-Ready' },
                                { text: 'Technologies ', highlight: true },
                                { text: 'for a' },
                                { text: 'Better World' },
                                { text: 'Today', highlight: true },
                            ]}
                        />
                    </div>
                </div>

                <div className='flex flex-row justify-end -mb-10 -translate-y-0 -translate-x-[20px] sm:-translate-x-5 md:-translate-x-10 xl:-translate-x-64 lg:-translate-x-48'>
                    <img src={IndiaFlag} alt="Flag of India" className='hero-flags rounded-full w-10 h-10 shadow-md' />
                    <img src={UKFlag} alt="Flag of the UK" className='hero-flags rounded-full w-10 h-10 -ml-2 shadow-md' />
                </div>

                <div className='mt-10' />
                <div className='hero-button'>
                    <Button1 width={"160px"} onClick={() => navigate('/about&portfolio')}>
                        Learn More
                    </Button1>
                </div>
            </div>

            <div className="flex flex-col md:flex-row gap-4 mx-auto justify-center max-w-7xl p-4 mt-12">
                <div className="hidden md:block md:w-1/5 hero-image-side">
                    <img src={Image1} alt="Column 1" className="w-full h-full object-cover rounded-3xl shadow-lg" />
                </div>
                <div className="w-full md:w-1/2 hero-image-main">
                    <img src={Image2} alt="Main hero" className="w-full h-full object-cover rounded-3xl shadow-lg" />
                </div>
                <div className="hidden md:block md:w-1/5 hero-image-side">
                    <img src={Image1} alt="Column 3" className="w-full h-full object-cover rounded-3xl shadow-lg" />
                </div>
            </div>

            <div className='mt-20 text-center px-4'>
                <div className='group-companies-heading'>
                    <DynamicHeading
                        parts={[
                            { text: 'Group' },
                            { text: 'Companies', highlight: true },
                        ]}
                    />
                </div>
                <div className='group-companies-subheading'>
                     <SubHeading>
                        Explore the specialized units powering our robotics, AI, and automation ecosystem.
                    </SubHeading>
                </div>
            </div>

            <motion.div
                className="grid grid-cols-1 mt-12 md:grid-cols-2 lg:grid-cols-3 gap-8 px-4 md:px-8"
                variants={containerVariants}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, amount: 0.2 }}
            >
                {groupCompanies.map((item, index) => (
                    <motion.div key={index} variants={itemVariants} className="company-card">
                        <CompaniesCardItem
                            logo={item.logo}
                            title={item.title}
                            description={item.description}
                        />
                    </motion.div>
                ))}
            </motion.div>

            <div className='mt-12 max-w-full overflow-hidden logo-ticker-section'>
                <LogoTicker />
            </div>

            <div className='mt-20 text-center px-4'>
                <div className='brands-heading'>
                    <DynamicHeading
                        parts={[
                            { text: 'Our' },
                            { text: 'Brands', highlight: true },
                        ]}
                    />
                </div>
                <div className='brands-subheading'>
                    <p className="font-light text-jbsGray max-w-3xl mx-auto mt-4 text-lg md:text-xl leading-relaxed">
                        Pioneering innovation through a family of specialized brands — each built to solve real-world challenges with smart, scalable technology.
                    </p>
                </div>
            </div>

            <div className="px-4 md:px-8 lg:px-16 py-10 w-full">
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 place-items-start">
                    {Brands.map((item, idx) => (
                        <div className="brand-card w-full flex justify-center" key={idx}>
                             <BrandsCardItem {...item} />
                        </div>
                    ))}
                </div>
            </div>

        </div>
    )
}

export default HomePage;